import type {
  CardBodyProps,
  CardProps,
  GridProps} from '@chakra-ui/react'
import {
  CardBody,
  Flex,
  forwardRef,
  Grid,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react'
import type { Offer } from 'BrokerTsLib/src/dtos'
import { formatDateTime } from 'BrokerTsLib/src/Utils/formatPrice'
import type { ReactNode} from 'react'
import { useEffect } from 'react'

import { Apis } from '@/appSettings'
import { useAuth } from '@/context/AuthContext'
import { useUrl } from '@/hooks'
import { useGetOfferDocuments } from '@/hooks/useDocuments'

import { RecapSummaryHeader } from './RecapSummaryHeader'

export interface RecapSummaryProps extends CardProps {
  number?: number | ReactNode
  sectionTitle?: string
  wrapperProps?: GridProps
  bodyProps?: CardBodyProps
  sidebar?: ReactNode
  offer?: Offer
}

export const RecapSummary = forwardRef(
  (
    {
      sectionTitle,
      number,
      children,
      wrapperProps,
      bodyProps,
      sidebar,
      offer,
      ...rest
    }: RecapSummaryProps,
    ref,
  ) => {

    const { user } = useAuth()
    const documentUrl = useUrl(Apis.document)
    const { refetch: getOfferDocuments, data: offerDocuments, isLoading: isLoadingDocs } = useGetOfferDocuments(offer!.offerId, { enabled: false, retry: false })
    const documents = offerDocuments?.data.filter(x => x.name !== 'OfferDocument')

    useEffect(() => {
      if (offer && user?.enableOfferXml)
        setTimeout(() => getOfferDocuments(), 2000)
    }, [offer, user?.enableOfferXml])

    return (
      <Grid
        ref={ref}
        sx={{
          alignItems: 'start',
          gridTemplateColumns: { base: 'auto', lg: '1fr 278px' }
        }}
        {...rest}
      >
        <Grid
          sx={{
            h: { lg: '100%' },
            alignContent: 'start',
            alignItems: 'start',
            ...wrapperProps?.sx,
          }}
        >
          <RecapSummaryHeader number={number}>
            <Flex justify="space-between" alignItems='center'>
              {sectionTitle}

              {(documents && documents.length > 0) && user?.enableOfferXml &&
                <Menu size="sm" placement='bottom'>
                  <MenuButton sx={{ gap: 2, whiteSpace: 'nowrap' }} type='button'>
                    XML
                  </MenuButton>
                  <MenuList>
                    {documents.map(doc => (
                      <MenuItem key={doc.id} as={'a'} target='_blank' href={`${documentUrl}/GetOfferDocument?docId=${doc.id}`}>
                        {doc.name} ({formatDateTime(doc.date, 'd.M.yyyy HH:mm:ss.SSS')})
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              }
            </Flex>
          </RecapSummaryHeader>

          <CardBody
            sx={{
              h: '100%',
              alignContent: 'start',
              alignItems: 'start',
              pr: { md: 8 },
              pl: { md: 8 },
              ...bodyProps?.sx,
            }}
          >
            {children}
          </CardBody>
        </Grid>

        {sidebar && sidebar}
      </Grid>
    )
  },
)
